import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { SiteLogo } from "@/components/SiteLogo";
import { BrandText } from "@/components/BrandText";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/app";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password.trim());
      navigate(redirectTo);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d && typeof d === "object" && (d.code === "locked" || d.code === "invalid")) {
        setError("Credenziali non valide.");
      } else {
        setError(formatApiErrorDetail(d) || err.message);
      }
    } finally {
      setLoading(false);
    }
  };
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSending, setForgotSending] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const sendForgot = async (e) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(forgotEmail)) { toast.error("Inserisci un'email valida"); return; }
    setForgotSending(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail.trim().toLowerCase() });
      setForgotSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nell'invio");
    } finally {
      setForgotSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-6 noise-bg">
      <div className="relative w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <SiteLogo className="w-9 h-9" />
          <div className="flex flex-col leading-tight">
            <BrandText />
          </div>
        </Link>
        <div className="rounded-2xl border border-white/10 bg-[#0F172A] p-8">
          <h1 className="font-display text-3xl">Bentornato</h1>
          <p className="text-muted-foreground text-sm mt-1">Accedi per gestire la competizione</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5" placeholder="admin@ginnastica.it" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5" placeholder="••••••••" />
            </div>
            {error && <p data-testid="login-error" className="text-sm text-destructive">{error}</p>}
            <Button data-testid="login-submit-btn" type="submit" disabled={loading} className="w-full bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Accedi"}
            </Button>
            <button type="button" data-testid="forgot-password-link" onClick={() => { setForgotOpen(true); setForgotSent(false); setForgotEmail(email); }} className="w-full text-center text-sm text-muted-foreground hover:text-gold transition-colors">
              Password dimenticata?
            </button>
          </form>
          <p className="text-sm text-muted-foreground mt-6 text-center">
            Non hai un account? <Link to="/register" data-testid="goto-register-link" className="text-gold hover:underline">Registrati</Link>
          </p>
        </div>
      </div>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent data-testid="forgot-password-dialog" className="bg-[#0F172A] border-white/10">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Password dimenticata</DialogTitle>
            <DialogDescription>Inserisci la tua email: ti invieremo un link per reimpostare la password (valido 1 ora).</DialogDescription>
          </DialogHeader>
          {forgotSent ? (
            <p className="text-sm text-foreground py-2" data-testid="forgot-sent-msg">Se esiste un account con questa email, riceverai a breve un messaggio con il link per reimpostare la password. Controlla anche la cartella SPAM/Promozioni.</p>
          ) : (
            <form onSubmit={sendForgot} className="space-y-4">
              <div>
                <Label htmlFor="forgot-email">Email</Label>
                <Input id="forgot-email" data-testid="forgot-email-input" type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} className="mt-1.5" placeholder="latua@email.it" />
              </div>
              <DialogFooter>
                <Button data-testid="forgot-send-btn" type="submit" disabled={forgotSending} className="bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold">
                  {forgotSending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Invia link di reset"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
